"use client";

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { signOut, useSession } from "next-auth/react";

// ─── Config ───────────────────────────────────────────────────────────────────
const TIMEOUT_MS =
  (Number(process.env.NEXT_PUBLIC_INACTIVITY_TIMEOUT_MINUTES) || 30) *
  60 *
  1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000; // show warning 2 min before logout
const WARNING_MS = TIMEOUT_MS - WARNING_BEFORE_MS;
const REFRESH_THROTTLE_MS = 15 * 60 * 1000; // max 1 server refresh per 15 minutes
const ACTIVITY_CHECK_INTERVAL_MS = 1000; // check every 1 second using wall-clock time

// ─── Component ────────────────────────────────────────────────────────────────

export default function InactivityGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120); // seconds until logout
  const [, startTransition] = useTransition();
  const { update, status } = useSession();

  const lastActivityTimeRef = useRef<number>(Date.now());
  const lastRefreshRef = useRef<number>(0);
  const lastCookieWriteRef = useRef<number>(0);
  const warningShownAtRef = useRef<number | null>(null);
  // Use a ref to track warning state INSIDE the event listener closure
  // so we don't need showWarning in the useEffect dependency array
  const showWarningRef = useRef(false);

  // ─── Throttled lastActivity storage update (client-only) ────────────────────
  const throttledCookieWrite = useCallback(() => {
    const now = Date.now();
    // Only write to localStorage at most once every 5 seconds on user activity
    if (now - lastCookieWriteRef.current < 5000) return;
    lastCookieWriteRef.current = now;
    try {
      localStorage.setItem("lastActivity", String(now));
    } catch (e) {
      /* ignore localStorage errors */
    }
  }, []);

  // ─── Session refresh (throttled) ──────────────────────────────────────────
  const throttledRefresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshRef.current < REFRESH_THROTTLE_MS) return;
    lastRefreshRef.current = now;
    // update lastActivity in localStorage when we refresh the session
    try {
      localStorage.setItem("lastActivity", String(now));
    } catch (e) {
      /* ignore localStorage errors */
    }

    startTransition(() => {
      update().catch(() => {
        /* session already expired */
      });
    });
  }, [update]);

  // ─── Reset activity timer (wall-clock based) ──────────────────────────────
  const resetActivityTimer = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    setShowWarning(false);
    showWarningRef.current = false;
    warningShownAtRef.current = null;
    setCountdown(120);
  }, []);

  // ─── Activity listener ─────────────────────────────────────────────────────
  useEffect(() => {
    // Don't run inactivity timer for users who aren't logged in
    if (status !== "authenticated") return;

    const handleActivity = () => {
      if (showWarningRef.current) return;
      throttledCookieWrite();
      throttledRefresh();
      resetActivityTimer();
    };

    const EVENTS = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ];
    EVENTS.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );

    // Initialize last activity time
    resetActivityTimer();

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ─── Wall-clock based inactivity checker ───────────────────────────────────
  // This uses actual elapsed time instead of setTimeout/setInterval,
  // so it survives system sleep/wake cycles
  useEffect(() => {
    if (status !== "authenticated") return;

    const checkInactivity = setInterval(() => {
      const now = Date.now();

      // Read lastActivity from localStorage to sync with other tabs
      let globalLastActivity = 0;
      try {
        globalLastActivity = parseInt(localStorage.getItem("lastActivity") || "0", 10);
      } catch (e) {
        /* ignore */
      }

      if (globalLastActivity > lastActivityTimeRef.current) {
        // Sync local activity time with the cookie updated by another tab
        lastActivityTimeRef.current = globalLastActivity;

        // If warning was showing, dismiss it
        if (showWarningRef.current) {
          setShowWarning(false);
          showWarningRef.current = false;
          warningShownAtRef.current = null;
          setCountdown(120);
        }
      }

      const timeSinceLastActivity = now - lastActivityTimeRef.current;

      // If warning is already showing, check if we should logout
      if (showWarningRef.current) {
        const warningShownAt = warningShownAtRef.current ?? now;
        const warningVisibleFor = now - warningShownAt;

        // Only auto-logout after the warning has actually been visible long enough.
        if (
          timeSinceLastActivity >= TIMEOUT_MS &&
          warningVisibleFor >= WARNING_BEFORE_MS
        ) {
          // Time to logout
          startTransition(() => {
            signOut({ redirect: false })
              .then(() => {
                window.location.href = "/signin";
              })
              .catch(() => {});
          });
        } else {
          // Update countdown
          const remainingMs = TIMEOUT_MS - timeSinceLastActivity;
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          setCountdown(Math.max(0, remainingSeconds));
        }
      } else {
        // Warning not showing yet, check if we should show it
        if (timeSinceLastActivity >= WARNING_MS) {
          setShowWarning(true);
          showWarningRef.current = true;
          warningShownAtRef.current = now;
          const remainingMs = TIMEOUT_MS - timeSinceLastActivity;
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          setCountdown(Math.max(0, remainingSeconds));
        }
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(checkInactivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ─── "Stay Logged In" handler ─────────────────────────────────────────────
  const handleStayLoggedIn = async () => {
    lastRefreshRef.current = 0; // force refresh

    // Immediately update the lastActivity in localStorage
    try {
      const now = Date.now();
      localStorage.setItem("lastActivity", String(now));
    } catch (e) {
      // ignore localStorage write errors
    }

    // Try a direct session refresh on user action
    // This will trigger the JWT callback which will refresh the access token if needed
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore-next-line: update() comes from useSession
      await update();

      // If refresh succeeded, reset timers and continue session
      resetActivityTimer();
    } catch (err) {
      // Refresh failed — force sign out and send user to sign-in page
      startTransition(() => {
        signOut({ redirect: false })
          .then(() => {
            window.location.href = "/signin";
          })
          .catch(() => {});
      });
    }
  };

  // ─── "Log Out" handler ────────────────────────────────────────────────────
  const handleLogOut = () => {
    startTransition(() => {
      signOut({ redirect: false })
        .then(() => {
          window.location.href = "/signin";
        })
        .catch(() => {});
    });
  };

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const formatted = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <>
      {children}

      {/* ─── Inactivity Warning Modal ─────────────────────────────────── */}
      {showWarning && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="inactivity-title"
          className="fixed inset-0 z-999999 flex h-dvh w-screen items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Modal */}
          <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900">
            {/* Icon */}
            <div className="mx-auto mb-4 flex text-text-primary h-14 w-14 items-center justify-center rounded-full bg-tertiary/25">
              <svg
                className="h-7 w-7 text-warning-500 dark:text-warning-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>

            <h2
              id="inactivity-title"
              className="mb-2 text-center text-lg font-semibold text-text-primary "
            >
              Session Expiring Soon
            </h2>

            <p className="mb-1 text-center text-sm text-text-secondary ">
              You&apos;ve been inactive. Your session will expire in:
            </p>

            {/* Countdown */}
            <div className="my-4 flex items-center justify-center">
              <span className="rounded-xl bg-brand-50 px-6 py-2 text-2xl font-bold tabular-nums text-primary/90 ">
                {formatted}
              </span>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleStayLoggedIn}
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary/85 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleLogOut}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-text-secondary transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
